import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Mail, Building, Truck, Search, Users, Eye, EyeOff, Copy, AlertCircle, Loader, X } from 'lucide-react';
import { useDistributors, useRegions, useIndustries, useBUs, useIndustriesName } from '../../../../app/modules/auth'
import { User } from './../types/user';
import { PageTitle } from '../../../../_metronic/layout/core';
import UserModal from './../components/UserModal';
import DeleteConfirmModal from './../components/DeleteConfirmModal';
import { apiService } from './../services/apiService';
//const apiService: any = {}

const ALL_VALUE = -9999;

export default function ApiUsers() {
    const distributorsData = useDistributors()
    const distributors:any = distributorsData.map((dist: any) => ({
        value: dist.value,
        label: dist.label,
    }));

    const industries = useIndustries()
    const industriesName = useIndustriesName()

    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [visibleTokens, setVisibleTokens] = useState<Set<string>>(new Set());
    const [copiedTokens, setCopiedTokens] = useState<Set<string>>(new Set());
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | undefined>();
    const [userToDelete, setUserToDelete] = useState<User | undefined>();
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Carregar usuários da API
    const loadUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const apiUsers:any = await apiService.getUsers();
            setUsers(apiUsers.data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao carregar usuários');
            console.error('Erro ao carregar usuários:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsers();
    }, []);

    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getIndustryName = (industryIds: string) => {
        if (!industryIds) return 'N/A';
        const ids = industryIds.split(',').map(id => parseInt(id.trim()));
        if (ids.length == 1 && ids[0] == ALL_VALUE) return 'Todos';
        const names = ids.map(id => {
            const industry = industries.find(industry => industry.value == id);
            return industry ? industry.label : id;
        });
        return names.join(', ');
    };

    const getCompanyName = (companyIds: string) => {
        if (!companyIds) return 'N/A';
        const ids = companyIds.split(',').map(id => parseInt(id.trim()));
        if (ids.length == 1 && ids[0] == ALL_VALUE) return 'Todos';
        const names = ids.map(id => {
        const distributor:any = distributors.find((d:any) => d.value == id);
            return distributor ? distributor.label : id;
        });
        return names.join(', ');
    };

    const handleCreateUser = () => {
        setSelectedUser(undefined);
        setIsUserModalOpen(true);
    };

    const handleEditUser = (user: User) => {
        setSelectedUser(user);
        setIsUserModalOpen(true);
    };

    const handleDeleteUser = (user: User) => {
        setUserToDelete(user);
        setIsDeleteModalOpen(true);
    };

    const handleSaveUser = async (userData: { name: string; email: string; industry: string; distributor: string }) => {
        try {
            setIsSubmitting(true);
            setError(null);

            const apiData = {
                name: userData.name,
                email: userData.email,
                industries: userData.industry,
                companies: userData.distributor
            };

            if (selectedUser) {
                await apiService.updateUser(selectedUser.guid, apiData);
            } else {
                await apiService.createUser(apiData);
            }

            setIsUserModalOpen(false);
            loadUsers();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao salvar usuário');
            console.error('Erro ao salvar usuário:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;

        try {
            setIsSubmitting(true);
            setError(null);
            
            await apiService.deleteUser(userToDelete.guid);
            setIsDeleteModalOpen(false);
            setUserToDelete(undefined);
            loadUsers();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao excluir usuário');
            console.error('Erro ao excluir usuário:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const copyAccessToken = async (token: string) => {
        try {
            await navigator.clipboard.writeText(token);
            setCopiedTokens(prev => new Set(prev).add(token));
            setTimeout(() => {
                setCopiedTokens(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(token);
                    return newSet;
                });
            }, 2000);
        } catch (error) {
            console.error('Failed to copy token:', error);
        }
    };

    const toggleTokenVisibility = (userId: string) => {
        setVisibleTokens(prev => {
        const newSet = new Set(prev);
        if (newSet.has(userId)) {
            newSet.delete(userId);
        } else {
            newSet.add(userId);
        }
        return newSet;
        });
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    if (loading) {
        return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
                <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-gray-600">Carregando usuários...</p>
            </div>
        </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <PageTitle>Gerenciamento de Usuários - API</PageTitle>
            <div className="mx-auto">
                {/* Error Alert */}
                {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center">
                        <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                        <div>
                            <h3 className="text-sm font-medium text-red-800">Erro</h3>
                            <p className="text-sm text-red-700 mt-1">{error}</p>
                        </div>
                        <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                )}

                {/* Search and Filters */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center space-x-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Buscar por nome ou e-mail..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div className="text-sm text-gray-500">
                            {filteredUsers.length} usuário{filteredUsers.length !== 1 ? 's' : ''} encontrado{filteredUsers.length !== 1 ? 's' : ''}
                        </div>
                        <button
                            onClick={handleCreateUser}
                            disabled={isSubmitting}
                            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ms-10"
                        >
                            <Plus className="w-4 h-4" />
                            <span>Novo Usuário</span>
                        </button>
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuário</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access Token</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Personal Key</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Indústrias</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distribuidores</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data Criação</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredUsers.map((user) => (
                                <tr key={user.guid} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                                {user.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="ml-3">
                                                <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                                <div className="text-sm text-gray-500 flex items-center">
                                                    <Mail className="w-3 h-3 mr-1" />
                                                    {user.email}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center space-x-2">
                                            <div className="bg-gray-100 rounded-md px-3 py-1 text-xs font-mono text-gray-700 max-w-xs truncate min-w-[200px]">
                                                {visibleTokens.has(user.guid) ? user.access_token : '••••••••••••••••••••••••••••••••••••'}
                                            </div>
                                            <button
                                                onClick={() => toggleTokenVisibility(user.guid)}
                                                className="text-gray-400 hover:text-blue-600 transition-colors"
                                                title={visibleTokens.has(user.guid) ? "Ocultar token" : "Mostrar token"}
                                            >
                                                {visibleTokens.has(user.guid) ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                            <button
                                                onClick={() => copyAccessToken(user.access_token)}
                                                className={`transition-colors ${
                                                    copiedTokens.has(user.access_token) 
                                                    ? 'text-green-600' 
                                                    : 'text-gray-400 hover:text-blue-600'
                                                }`}
                                                title="Copiar token"
                                                >
                                                <Copy className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center space-x-2">
                                            <div className="bg-gray-100 rounded-md px-3 py-1 text-xs font-mono text-gray-700 max-w-xs truncate min-w-[200px]">
                                                {visibleTokens.has(user.guid) ? user.personal_key : '••••••••••••••••••••••••••••••••••••'}
                                            </div>
                                            <button
                                                onClick={() => toggleTokenVisibility(user.guid)}
                                                className="text-gray-400 hover:text-blue-600 transition-colors"
                                                title={visibleTokens.has(user.guid) ? "Ocultar token" : "Mostrar token"}
                                            >
                                                {visibleTokens.has(user.guid) ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                            <button
                                                onClick={() => copyAccessToken(user.personal_key)}
                                                className={`transition-colors ${
                                                    copiedTokens.has(user.personal_key) 
                                                    ? 'text-green-600' 
                                                    : 'text-gray-400 hover:text-blue-600'
                                                }`}
                                                title="Copiar token"
                                                >
                                                <Copy className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center text-sm text-gray-900">
                                            <Building className="w-4 h-4 mr-2 text-gray-400" />
                                            <span className="max-w-xs truncate">{getIndustryName(user.industries)}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center text-sm text-gray-900">
                                            <Truck className="w-4 h-4 mr-2 text-gray-400" />
                                            <span className="max-w-xs truncate">{getCompanyName(user.companies)}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatDate(user.created_at)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center justify-end space-x-2">
                                            <button
                                                onClick={() => handleEditUser(user)}
                                                disabled={isSubmitting}
                                                className="text-blue-600 hover:text-blue-900 disabled:text-blue-400 transition-colors p-1"
                                                title="Editar usuário"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteUser(user)}
                                                disabled={isSubmitting}
                                                className="text-red-600 hover:text-red-900 disabled:text-red-400 transition-colors p-1"
                                                title="Excluir usuário"
                                                >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {filteredUsers.length === 0 && !loading && (
                    <div className="text-center py-12">
                        <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
                        </h3>
                        <p className="text-gray-500 mb-4">
                            {searchTerm 
                            ? 'Tente ajustar o texto da busca'
                            : 'Comece criando o primeiro usuário da API'
                            }
                        </p>
                        {!searchTerm && (
                        <button
                            onClick={handleCreateUser}
                            disabled={isSubmitting}
                            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center space-x-2 mx-auto transition-colors"
                            >
                            <Plus className="w-4 h-4" />
                            <span>Criar Primeiro Usuário</span>
                        </button>
                        )}
                    </div>)}
                </div>
            </div>

            {/* Modals */}
            <UserModal
                isOpen={isUserModalOpen}
                onClose={() => setIsUserModalOpen(false)}
                onSave={handleSaveUser}
                user={selectedUser}
                industries={industries}
                distributors={distributors}
                isSubmitting={isSubmitting}
            />

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                userName={userToDelete?.name || ''}
                isSubmitting={isSubmitting}
            />
        </div>
    );
}